import { Suspense } from 'react';
import '@admin/i18n';

import { CssBaseline, MuiThemeProvider } from '@material-ui/core';

import { BackendWrapper, ErrorBoundary } from '@omq/shared';

import { createTheme } from '@admin/shared/themes/default';
import { DialogWrapper } from '@admin/shared/context/dialog';
import { createUserlikeTheme } from '@admin/shared/themes/integrations/userlike';
import { createMappTheme } from '@admin/shared/themes/integrations/mapp';
import { ContractWrapper } from '@admin/shared/context/contract';
import { SupportWrapper } from '@admin/shared/context/support';
import { AppLoadingScreen } from '@admin/shared/components/progress/app-loading-screen';
import { Themes } from '@admin/shared/themes/themes';
import { Banner } from '@admin/shared/components/module/banner';

/**
 * Type for component properties
 */
type AppProps = {
  theme?: {};
  children: JSX.Element;
};

function detectTheme(): string {
  return window.theme || Themes.OMQ;
}

/**
 * App wrapper for each module.
 *
 * @param {Theme} theme - module theme
 * @param {JSX.Element} children - children to render
 *
 * @returns {JSX.Element}
 */
export const App = ({ theme, children }: AppProps): JSX.Element => {
  const themeName = detectTheme();
  let currentTheme;

  // create theme depending on passed theme name
  switch (themeName) {
    case Themes.USERLIKE:
      currentTheme = createUserlikeTheme();
      break;
    case Themes.MAPP:
      currentTheme = createMappTheme();
      break;
    default:
      currentTheme = theme || createTheme();
  }

  // StrictMode is disabled,
  // turn back on later

  return (
    <ErrorBoundary>
      <Suspense fallback={<AppLoadingScreen />}>
        <MuiThemeProvider theme={currentTheme}>
          <BackendWrapper account="">
            <DialogWrapper>
              <ContractWrapper>
                <SupportWrapper>
                  <Banner />
                  <CssBaseline />
                  {children}
                </SupportWrapper>
              </ContractWrapper>
            </DialogWrapper>
          </BackendWrapper>
        </MuiThemeProvider>
      </Suspense>
    </ErrorBoundary>
  );
};
